// src/pages/Patients.js
// Patient List UI - View and manage all patients

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useDemoTour } from '../contexts';
import { supabase } from '../lib/supabaseClient';
import * as awsApi from '../services/awsApiService';
import * as apiService from '../services/apiService';
import { logPHIAccess, logWorkflowExecution } from '../services/auditService';
import ResultsDisplay from '../components/ResultsDisplay';
import { demoPatientFinancials, demoPatients, demoClaimsByPatientId } from '../data/demoData';
import {
  Users,
  Search,
  Phone,
  Mail,
  Calendar,
  FileText,
  AlertCircle,
  RefreshCw,
  Upload,
  Trash2,
  Eye,
  X,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Plus,
  ShieldCheck,
  Zap,
  Pencil
} from 'lucide-react';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';

const EMPTY_FORM = {
  first_name: '', last_name: '', date_of_birth: '', email: '', phone: '',
  insurance_primary_carrier: '', insurance_member_id: '', notes: '',
  secondary_insurance_carrier: '', secondary_insurance_id: '', secondary_group_number: ''
};

const Patients = () => {
  const navigate = useNavigate();
  const { user, practice } = useAuth();
  const { currentStep } = useDemoTour() || {};
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, activeClaims: 0 });
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);

  // Add patient state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [eligibilityChecking, setEligibilityChecking] = useState(false);
  const [autoCheckEligibility, setAutoCheckEligibility] = useState(false);

  // Edit patient state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Ledger state
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientClaims, setPatientClaims] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Benefits verification state (for existing patient ledger)
  const [benefitsLoading, setBenefitsLoading] = useState(false);
  const [benefitsResult, setBenefitsResult] = useState(null);
  const [payerIdOverride, setPayerIdOverride] = useState('');

  // Patient Financial AI state
  const [financialAILoading, setFinancialAILoading] = useState(false);
  const [financialAIResults, setFinancialAIResults] = useState(null);
  const [financialAIProcessingTime, setFinancialAIProcessingTime] = useState(null);
  const [financialAIForm, setFinancialAIForm] = useState({
    InsuranceStatus: 'Active Coverage',
    PaymentHistory: 'New Patient (No History)',
  });

  useEffect(() => {
    loadPatients();
  }, [practice, showArchived]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPatients(patients);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = patients.filter(p =>
        (p.first_name?.toLowerCase() || '').includes(term) ||
        (p.last_name?.toLowerCase() || '').includes(term) ||
        (p.email?.toLowerCase() || '').includes(term) ||
        (p.phone?.toLowerCase() || '').includes(term) ||
        (p.open_dental_patient_id?.toLowerCase() || '').includes(term)
      );
      setFilteredPatients(filtered);
    }
  }, [searchTerm, patients]);

  // Demo tour: auto-open Sarah's ledger for balance and Financial AI steps
  useEffect(() => {
    if (apiService.getAppMode() !== 'demo') return;
    const id = currentStep?.id;
    const isBalanceStep = id === 'workflow-patient-balance';
    const isAIStep = id === 'workflow-financial-ai' || id === 'financial-ai';
    const isPatientListStep = id === 'patient-list';
    if (!isBalanceStep && !isAIStep && !isPatientListStep) return;
    // In demo mode use a hardcoded Sarah Mitchell fallback if not in DB
    const DEMO_SARAH = {
      id: 'demo-sarah-mitchell', first_name: 'Sarah', last_name: 'Mitchell',
      date_of_birth: '1989-04-12', phone: '555-321-7890', email: 'sarah.mitchell@email.com',
      insurance_primary_carrier: 'MetLife Dental', outstanding_balance: 112.00,
    };
    const sarah = patients.find(p => p.last_name === 'Mitchell') || DEMO_SARAH;
    // For balance step: inject mock data directly — skip openLedger to avoid race condition
    if (isBalanceStep) {
      const t1 = setTimeout(() => {
        setSelectedPatient(sarah);
        setLedgerLoading(false);
        setBenefitsResult(null);
        setFinancialAIResults(null);
        setPatientClaims([{
          id: 'demo-claim-sarah',
          claim_number: 'CLM-2026-0318',
          claim_status: 'Paid',
          claim_amount: 890,
          claim_paid_amount: 778,
          procedure_codes: ['D2750'],
          service_date: '2026-03-18',
          payer_name: 'MetLife Dental',
          patient_name: 'Sarah Mitchell',
        }]);
      }, 400);
      return () => clearTimeout(t1);
    }
    // For AI steps: inject mock claim data directly (skip openLedger to avoid real DB data overriding)
    const t1 = setTimeout(() => {
      setSelectedPatient(sarah);
      setLedgerLoading(false);
      setBenefitsResult(null);
      setFinancialAIResults(null);
      setPatientClaims([{
        id: 'demo-claim-sarah',
        claim_number: 'CLM-2026-0318',
        claim_status: 'Paid',
        claim_amount: 890,
        claim_paid_amount: 778,
        procedure_codes: ['D2750'],
        service_date: '2026-03-18',
        payer_name: 'MetLife Dental',
        patient_name: 'Sarah Mitchell',
      }]);
    }, 400);
    const t2 = setTimeout(() => {
      setFinancialAIResults(demoPatientFinancials);
      setFinancialAIProcessingTime('1.7');
    }, 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [currentStep?.id, patients]); // eslint-disable-line

  // Demo tour: patient-list step — cursor clicks Sarah's row, override DB claims with mock after ledger opens
  useEffect(() => {
    if (apiService.getAppMode() !== 'demo') return;
    if (currentStep?.id !== 'patient-list') return;
    // Inject mock claims 3.5s after mount (after cursor click + openLedger finishes)
    const t1 = setTimeout(() => {
      setPatientClaims([{
        id: 'demo-claim-sarah',
        claim_number: 'CLM-2026-0318',
        claim_status: 'Paid',
        claim_amount: 890,
        claim_paid_amount: 778,
        procedure_codes: ['D2750'],
        service_date: '2026-03-18',
        payer_name: 'MetLife Dental',
        patient_name: 'Sarah Mitchell',
      }]);
      setLedgerLoading(false);
    }, 3500);
    return () => clearTimeout(t1);
  }, [currentStep?.id]); // eslint-disable-line

  const loadPatients = async () => {
    setLoading(true);
    setError(null);

    const practiceId = useAWS ? practice?.practice_id : practice?.id;
    if (!practiceId && !useAWS) {
      setLoading(false);
      return;
    }

    try {
      let data = [];

      if (useAWS) {
        // In demo mode, race the API call against a 2.5s timeout so slow DB doesn't stall the UI
        const fetchPromise = awsApi.getPatients(practiceId, {
          limit: 200,
          archived: showArchived ? 'true' : 'false'
        });
        const isDemoMode = apiService.getAppMode() === 'demo';
        const result = isDemoMode
          ? await Promise.race([fetchPromise, new Promise(r => setTimeout(() => r({ success: true, data: [] }), 2500))])
          : await fetchPromise;
        if (result.success) {
          data = result.data || [];
        } else {
          throw new Error(result.error || 'Failed to load patients');
        }
        // Demo mode fallback: if no DB data, use demo patients so tour always works
        if (data.length === 0 && isDemoMode) {
          data = demoPatients;
          // Use realistic demo stats directly — skip DB analytics call
          setPatients(data);
          setFilteredPatients(data);
          setStats({ total: 15, thisMonth: 3, activeClaims: 12 });
          logPHIAccess('patient_list', null, 'view');
          setLoading(false);
          return;
        }
      } else {
        const { data: supabaseData, error: queryError } = await supabase
          .from('patients')
          .select('*')
          .eq('practice_id', practiceId)
          .order('created_at', { ascending: false })
          .limit(200);

        if (queryError) throw new Error(queryError.message);
        data = supabaseData || [];
      }

      setPatients(data);
      setFilteredPatients(data);
      logPHIAccess('patient_list', null, 'view');

      const thisMonthStart = new Date();
      thisMonthStart.setDate(1);
      thisMonthStart.setHours(0, 0, 0, 0);
      const thisMonthPatients = data.filter(p => new Date(p.created_at) >= thisMonthStart);

      let activeClaims = 0;
      if (useAWS) {
        const analyticsResult = await awsApi.getAnalytics(practiceId);
        if (analyticsResult.success) {
          activeClaims = analyticsResult.data.claims?.pending || 0;
        }
      }

      setStats({ total: data.length, thisMonth: thisMonthPatients.length, activeClaims });
    } catch (err) {
      if (apiService.getAppMode() === 'demo') {
        setPatients(demoPatients);
        setFilteredPatients(demoPatients);
        setStats({ total: demoPatients.length, thisMonth: 3, activeClaims: 12 });
      } else {
        setError(err.message || 'Query timeout - please refresh the page');
        setPatients([]);
        setFilteredPatients([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunFinancialAI = async (patient) => {
    setFinancialAILoading(true);
    setFinancialAIResults(null);
    const startTime = Date.now();
    try {
      const patientData = {
        PatientID: patient.open_dental_patient_id || patient.id?.slice(0, 8),
        patientId: patient.open_dental_patient_id || patient.id?.slice(0, 8),
        BalanceAmount: patient.outstanding_balance?.toString() || '0',
        InsuranceStatus: financialAIForm.InsuranceStatus,
        PaymentHistory: financialAIForm.PaymentHistory,
        _patientName: `${patient.first_name} ${patient.last_name}`,
      };
      const response = await apiService.analyzePatientFinancials(patientData);
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      setFinancialAIProcessingTime(response.processingTime || elapsed);
      setFinancialAIResults(response);
      logWorkflowExecution('Patient Financial AI', patientData.PatientID, response).catch(() => {});
    } catch (err) {
      setFinancialAIResults({ success: false, error: err.message });
    } finally {
      setFinancialAILoading(false);
    }
  };

  const verifyPatientBenefits = async (patient) => {
    setBenefitsLoading(true);
    setBenefitsResult(null);
    try {
      const res = await awsApi.verifyBenefits({
        patient_first_name: patient.first_name,
        patient_last_name: patient.last_name,
        patient_dob: patient.date_of_birth,
        insurance_carrier: patient.insurance_carrier || patient.insurance_provider,
        payer_id_override: payerIdOverride || undefined,
        member_id: patient.insurance_id || patient.insurance_member_id,
      });
      setBenefitsResult(res.success ? res.data : { error: res.error || 'Verification failed' });
    } catch (e) {
      setBenefitsResult({ error: e.message });
    } finally {
      setBenefitsLoading(false);
    }
  };

  const openLedger = async (patient) => {
    setSelectedPatient(patient);
    setPatientClaims([]);
    setFinancialAIResults(null);
    setBenefitsResult(null);
    setFinancialAIProcessingTime(null);
    setFinancialAIForm({ InsuranceStatus: 'Active Coverage', PaymentHistory: 'New Patient (No History)' });
    setLedgerLoading(true);
    logPHIAccess('patient_ledger', patient.id, 'view');
    // Demo mode: use mock claims for all demo patients
    if (apiService.getAppMode() === 'demo' && patient.id?.startsWith('demo-')) {
      const mockClaims = demoClaimsByPatientId[patient.id] || [];
      setPatientClaims(mockClaims);
      setLedgerLoading(false);
      return;
    }
    try {
      const result = await awsApi.getClaimsByPatient(patient.id, practice?.practice_id);
      if (result.success) {
        setPatientClaims(result.data || []);
      }
    } catch (err) {
      // show empty ledger if claims fail
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleArchive = async (patientId) => {
    setConfirmArchive(null);
    setArchiving(patientId);
    try {
      if (useAWS) {
        const result = await awsApi.archivePatient(patientId);
        if (!result.success) throw new Error(result.error);
      } else {
        const { error } = await supabase.from('patients').update({ active: false }).eq('id', patientId);
        if (error) throw error;
      }
      loadPatients();
    } catch (err) {
      setError('Failed to archive patient: ' + err.message);
    } finally {
      setArchiving(null);
    }
  };

  const handleRestore = async (patientId) => {
    setArchiving(patientId);
    try {
      if (useAWS) {
        const result = await awsApi.restorePatient(patientId);
        if (!result.success) throw new Error(result.error);
      } else {
        const { error } = await supabase.from('patients').update({ active: true }).eq('id', patientId);
        if (error) throw error;
      }
      loadPatients();
    } catch (err) {
      setError('Failed to restore patient: ' + err.message);
    } finally {
      setArchiving(null);
    }
  };

  const handleAddPatient = async () => {
    if (!addForm.first_name || !addForm.last_name) {
      setAddError('First name and last name are required.');
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const result = await awsApi.createPatient({
        ...addForm,
        practice_id: practice?.practice_id
      });
      if (!result.success) throw new Error(result.error);
      const newPatient = result.data;

      // Auto-check eligibility if enabled and insurance info provided
      if (autoCheckEligibility && addForm.insurance_primary_carrier && addForm.date_of_birth) {
        setEligibilityChecking(true);
        try {
          const eligRes = await awsApi.verifyBenefits({
            patient_first_name: addForm.first_name,
            patient_last_name: addForm.last_name,
            patient_dob: addForm.date_of_birth,
            patient_member_id: addForm.insurance_member_id || '',
            insurance_carrier: addForm.insurance_primary_carrier,
            practice_id: practice?.practice_id
          });
          if (eligRes.success) setEligibilityResult(eligRes.data);
        } catch (_) {
          // eligibility check failure is non-blocking
        } finally {
          setEligibilityChecking(false);
        }
      }

      setAddForm(EMPTY_FORM);
      setShowAddModal(false);
      loadPatients();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAddSaving(false);
    }
  };

  const openEditModal = (patient, e) => {
    e.stopPropagation();
    setEditPatient(patient);
    setEditForm({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      date_of_birth: patient.date_of_birth ? patient.date_of_birth.split('T')[0] : '',
      email: patient.email || '',
      phone: patient.phone || '',
      insurance_primary_carrier: patient.insurance_carrier || patient.insurance_provider || patient.insurance_primary_carrier || '',
      insurance_member_id: patient.insurance_id || patient.insurance_member_id || '',
      notes: patient.notes || '',
      secondary_insurance_carrier: patient.secondary_insurance_carrier || '',
      secondary_insurance_id: patient.secondary_insurance_id || '',
      secondary_group_number: patient.secondary_group_number || '',
    });
    setEditError(null);
    setShowEditModal(true);
  };

  const handleEditPatient = async () => {
    if (!editForm.first_name || !editForm.last_name) {
      setEditError('First name and last name are required.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const result = await awsApi.updatePatient(editPatient.id, editForm);
      if (!result.success) throw new Error(result.error);
      setShowEditModal(false);
      setEditPatient(null);
      loadPatients();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'UTC' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    return phone;
  };

  const getStatusBadge = (status) => {
    const config = {
      'Paid':      { bg: 'bg-green-100',  text: 'text-green-700',  icon: CheckCircle },
      'Pending':   { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      'Submitted': { bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Clock },
      'In Review': { bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Clock },
      'Denied':    { bg: 'bg-red-100',    text: 'text-red-700',    icon: XCircle },
      'Appealed':  { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertTriangle },
    }[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText };
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />{status || 'Unknown'}
      </span>
    );
  };

  // Ledger summary calculations
  const ledgerSummary = patientClaims.reduce((acc, c) => {
    acc.totalBilled += parseFloat(c.claim_amount || 0);
    acc.totalPaid   += parseFloat(c.claim_paid_amount || 0);
    acc.denied      += c.claim_status === 'Denied' ? 1 : 0;
    acc.pending     += ['Pending','Submitted','In Review'].includes(c.claim_status) ? 1 : 0;
    return acc;
  }, { totalBilled: 0, totalPaid: 0, denied: 0, pending: 0 });

  ledgerSummary.balance = ledgerSummary.totalBilled - ledgerSummary.totalPaid;
  ledgerSummary.collectionRate = ledgerSummary.totalBilled > 0
    ? Math.round((ledgerSummary.totalPaid / ledgerSummary.totalBilled) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Patient Management</h1>
          <p className="text-sm text-slate-500">View and manage all patients in your practice</p>
        </div>
        <button onClick={() => navigate('/data-import')} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
          <Upload className="w-4 h-4" /> Import Patients
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center"><Users className="w-6 h-6 text-teal-600" /></div>
              <div><p className="text-slate-600 text-sm">Total Patients</p><p className="text-2xl font-bold text-slate-900">{stats.total}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><Calendar className="w-6 h-6 text-blue-600" /></div>
              <div><p className="text-slate-600 text-sm">Added This Month</p><p className="text-2xl font-bold text-slate-900">{stats.thisMonth}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><FileText className="w-6 h-6 text-purple-600" /></div>
              <div><p className="text-slate-600 text-sm">Active Claims</p><p className="text-2xl font-bold text-slate-900">{stats.activeClaims}</p></div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg border bg-red-50 border-red-200 text-red-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{error}</span>
              <button onClick={loadPatients} className="ml-auto flex items-center gap-1 text-red-600 hover:text-red-800">
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="p-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or patient ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div data-tour="patient-list" className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              {showArchived ? 'Archived Patients' : 'Patients'} ({filteredPatients.length})
            </h2>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                Show Archived
              </label>
              <button onClick={loadPatients} className="flex items-center gap-2 text-slate-600 hover:text-slate-800">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <button
                onClick={() => { setShowAddModal(true); setAddError(null); setEligibilityResult(null); }}
                className="flex items-center gap-2 bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-800"
              >
                <Plus className="w-4 h-4" /> Add Patient
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-600 mt-4">Loading patients...</p>
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              {searchTerm ? (
                <p className="text-slate-600 font-medium">No patients match your search</p>
              ) : (
                <>
                  <p className="text-slate-600 font-medium">No patients yet</p>
                  <button onClick={() => navigate('/data-import')} className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">Import Patients</button>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Patient</th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-slate-600">Insurance / Member ID</th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-slate-600">Date of Birth</th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-slate-600">Contact</th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-slate-600">Patient ID</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 sticky right-0 bg-slate-50">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPatients.map((patient) => (
                    <tr key={patient.id} data-patient-name={patient.last_name} data-tour={apiService.getAppMode() === 'demo' && patient.first_name === 'Sarah' && patient.last_name === 'Mitchell' ? 'sarah-patient-row' : undefined} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => openLedger(patient)}>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-teal-700 font-semibold text-sm">
                              {(patient.first_name || 'U').charAt(0).toUpperCase()}{(patient.last_name || '').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-3 text-sm text-slate-700">
                        <div>{patient.insurance_carrier || patient.insurance_provider || <span className="text-slate-400">—</span>}</div>
                        {(patient.insurance_id || patient.insurance_member_id) && <div className="text-xs font-mono text-slate-500 mt-0.5">{patient.insurance_id || patient.insurance_member_id}</div>}
                      </td>
                      <td className="py-4 px-3 text-slate-700">
                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" />{formatDate(patient.date_of_birth)}</div>
                      </td>
                      <td className="py-4 px-3">
                        <div className="space-y-1">
                          {patient.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-3 h-3 flex-shrink-0" />{formatPhone(patient.phone)}</div>}
                          {patient.email && <div className="flex items-center gap-2 text-sm text-slate-600 max-w-[160px]"><Mail className="w-3 h-3 flex-shrink-0" /><span className="truncate">{patient.email}</span></div>}
                          {!patient.phone && !patient.email && <span className="text-slate-400 text-sm">—</span>}
                        </div>
                      </td>
                      <td className="py-4 px-3"><span className="text-sm font-mono text-slate-600">{patient.open_dental_patient_id || patient.id?.slice(0, 8) || 'N/A'}</span></td>
                      <td className="py-4 px-4 sticky right-0 bg-white" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openLedger(patient)} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="View Ledger">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={(e) => openEditModal(patient, e)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors" title="Edit Patient">
                            <Pencil className="w-4 h-4" />
                          </button>
                          {showArchived ? (
                            <button onClick={() => handleRestore(patient.id)} disabled={archiving === patient.id} className="flex items-center gap-1 px-3 py-1 text-sm bg-teal-100 text-teal-700 rounded hover:bg-teal-200 transition-colors disabled:opacity-50">
                              <RefreshCw className={`w-3 h-3 ${archiving === patient.id ? 'animate-spin' : ''}`} /> Restore
                            </button>
                          ) : confirmArchive === patient.id ? (
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleArchive(patient.id)} disabled={archiving === patient.id} className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">Confirm</button>
                              <button onClick={() => setConfirmArchive(null)} className="px-3 py-1 text-sm bg-slate-100 text-slate-600 rounded hover:bg-slate-200">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmArchive(patient.id)} disabled={archiving === patient.id} className="flex items-center gap-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50">
                              <Trash2 className="w-3 h-3" /> Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Patient Ledger Modal */}
      {selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">

            {/* Modal Header */}
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-white flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-teal-700 font-bold text-lg">
                    {(selectedPatient.first_name || 'U').charAt(0).toUpperCase()}{(selectedPatient.last_name || '').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedPatient.first_name} {selectedPatient.last_name}</h2>
                  <p className="text-sm text-slate-500">Patient Ledger</p>
                </div>
              </div>
              <button onClick={() => setSelectedPatient(null)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1"><div className="p-6 space-y-6">

              {/* Patient Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-slate-500">Date of Birth</p><p className="font-medium text-slate-900">{formatDate(selectedPatient.date_of_birth)}</p></div>
                <div><p className="text-slate-500">Primary Insurance</p><p className="font-medium text-slate-900">{selectedPatient.insurance_carrier || selectedPatient.insurance_provider || 'N/A'}</p></div>
                <div><p className="text-slate-500">Phone</p><p className="font-medium text-slate-900">{formatPhone(selectedPatient.phone)}</p></div>
                <div><p className="text-slate-500">Email</p><p className="font-medium text-slate-900">{selectedPatient.email || 'N/A'}</p></div>
                {selectedPatient.secondary_insurance_carrier && (
                  <div className="col-span-2 mt-1 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Secondary Insurance</p>
                    <p className="font-medium text-slate-900 text-sm">{selectedPatient.secondary_insurance_carrier}</p>
                    {selectedPatient.secondary_insurance_id && <p className="text-xs text-slate-500">Member ID: {selectedPatient.secondary_insurance_id}</p>}
                    {selectedPatient.secondary_group_number && <p className="text-xs text-slate-500">Group: {selectedPatient.secondary_group_number}</p>}
                  </div>
                )}
              </div>

              {/* Benefits Verification */}
              <div className="border border-teal-200 rounded-lg overflow-hidden">
                <div className="bg-teal-600 px-4 py-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-white" />
                    <span className="text-white font-semibold text-sm">Benefits Verification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={payerIdOverride}
                      onChange={e => setPayerIdOverride(e.target.value)}
                      placeholder="Payer ID override"
                      className="text-xs px-2 py-1.5 rounded-md border-0 w-32 text-slate-700 placeholder-slate-400"
                    />
                    <button
                      onClick={() => verifyPatientBenefits(selectedPatient)}
                      disabled={benefitsLoading}
                      className="flex items-center gap-1.5 bg-white text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-teal-50 disabled:opacity-60 transition-colors"
                    >
                      {benefitsLoading ? <><RefreshCw className="w-3 h-3 animate-spin" /> Verifying...</> : <><Zap className="w-3 h-3" /> Verify Now</>}
                    </button>
                  </div>
                </div>
                <div className="p-4 bg-teal-50 text-sm">
                  {!benefitsResult && !benefitsLoading && (
                    <p className="text-teal-700 text-xs">Click Verify Now to check real-time eligibility and coverage with {selectedPatient.insurance_carrier || selectedPatient.insurance_provider || 'the payer'}.</p>
                  )}
                  {benefitsLoading && (
                    <div className="flex items-center gap-2 text-teal-700 text-xs">
                      <RefreshCw className="w-3 h-3 animate-spin" /> Checking eligibility with payer...
                    </div>
                  )}
                  {benefitsResult && !benefitsResult.error && (
                    <div className="space-y-2">
                      {benefitsResult.InsuranceStatus && (
                        <div className="flex items-center gap-2">
                          {benefitsResult.InsuranceStatus === 'Active Coverage'
                            ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          <span className="font-semibold text-slate-800">{benefitsResult.InsuranceStatus}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        {benefitsResult.PlanName && <p className="text-slate-600">Plan: <span className="font-medium text-slate-800">{benefitsResult.PlanName}</span></p>}
                        {benefitsResult.GroupNumber && <p className="text-slate-600">Group: <span className="font-medium text-slate-800">{benefitsResult.GroupNumber}</span></p>}
                        {benefitsResult.Deductible && <p className="text-slate-600">Deductible: <span className="font-medium text-slate-800">{benefitsResult.Deductible}</span></p>}
                        {benefitsResult.AnnualMaximum && <p className="text-slate-600">Annual Max: <span className="font-medium text-slate-800">{benefitsResult.AnnualMaximum}</span></p>}
                        {benefitsResult.RemainingBenefit && <p className="col-span-2 text-slate-600">Remaining: <span className="font-semibold text-green-700">{benefitsResult.RemainingBenefit}</span></p>}
                      </div>
                      {benefitsResult.coverage?.coverageLevels?.length > 0 && (
                        <div className="border-t border-teal-100 pt-2 mt-1">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Coverage by Category</p>
                          <div className="space-y-1">
                            {benefitsResult.coverage.coverageLevels.map((lvl, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-slate-600">{lvl.category}</span>
                                <span className={`font-semibold ${lvl.coverage === 'Not Covered' ? 'text-red-500' : 'text-teal-700'}`}>{lvl.coverage}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {benefitsResult?.error && (
                    <div className="flex items-center gap-2 text-red-600 text-xs">
                      <AlertCircle className="w-3 h-3" /> {benefitsResult.error}
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Summary */}
              {!ledgerLoading && patientClaims.length > 0 && (
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Total Billed</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(ledgerSummary.totalBilled)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Total Paid</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(ledgerSummary.totalPaid)}</p>
                  </div>
                  <div data-tour="sarah-balance-card" className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Outstanding</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(ledgerSummary.balance)}</p>
                    {apiService.getAppMode() === 'demo' && selectedPatient?.first_name === 'Sarah' && (
                      <p
                        key={currentStep?.id}
                        className="text-xs text-emerald-600 mt-1 font-medium"
                        style={{ animation: 'demoBadgeIn 0.55s ease-out, pulseGlowGreen 2s ease-in-out 0.55s infinite' }}
                      >✓ Payment link sent 11 days ago</p>
                    )}
                  </div>
                  <div className="bg-teal-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Collection Rate</p>
                    <p className="text-lg font-bold text-teal-700">{ledgerSummary.collectionRate}%</p>
                  </div>
                </div>
              )}

              {/* Claims History */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-teal-600" /> Claims History
                  {patientClaims.length > 0 && <span className="text-sm font-normal text-slate-500">({patientClaims.length} claims)</span>}
                </h3>

                {ledgerLoading ? (
                  <div className="py-8 text-center">
                    <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-slate-500 text-sm mt-3">Loading claims...</p>
                  </div>
                ) : patientClaims.length === 0 ? (
                  <div className="py-8 text-center bg-slate-50 rounded-lg">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">No claims found for this patient.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {patientClaims.map((claim) => (
                      <div key={claim.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-teal-300 transition-colors">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{claim.payer_name || claim.payer || 'Unknown Payer'}</p>
                            <p className="text-xs text-slate-500">{formatDate(claim.service_date)} · {claim.procedure_codes || 'N/A'}</p>
                            {claim.denial_reason && (
                              <p className="text-xs text-red-600 mt-0.5">Denial: {claim.denial_reason}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-right">
                          <div>
                            <p className="text-sm font-bold text-slate-900">{formatCurrency(claim.claim_amount)}</p>
                            {parseFloat(claim.claim_paid_amount) > 0 && (
                              <p className="text-xs text-green-600">Paid: {formatCurrency(claim.claim_paid_amount)}</p>
                            )}
                          </div>
                          {getStatusBadge(claim.claim_status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* AI Financial Analysis Panel */}
            <div className="px-6 pb-6">
              <div data-tour="financial-ai" className="rounded-lg border border-orange-200 overflow-hidden">
                <div className="bg-orange-500 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-white" />
                    <span className="text-white font-semibold text-sm">Patient Financial AI</span>
                  </div>
                  {financialAIResults && (
                    <button
                      onClick={() => { setFinancialAIResults(null); setFinancialAIProcessingTime(null); }}
                      className="text-xs text-orange-200 hover:text-white underline"
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div className="p-4 bg-orange-50">
                  {!financialAIResults ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">Insurance Status</label>
                          <select
                            value={financialAIForm.InsuranceStatus}
                            onChange={e => setFinancialAIForm(f => ({ ...f, InsuranceStatus: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white"
                          >
                            {['Active Coverage', 'No Coverage', 'Coverage Lapsed', 'Unknown'].map(o => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">Payment History</label>
                          <select
                            value={financialAIForm.PaymentHistory}
                            onChange={e => setFinancialAIForm(f => ({ ...f, PaymentHistory: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white"
                          >
                            {['Excellent (Always On Time)', 'Good (Mostly On Time)', 'Fair (Some Late Payments)', 'Poor (Frequently Late)', 'New Patient (No History)'].map(o => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRunFinancialAI(selectedPatient)}
                        disabled={financialAILoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {financialAILoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4" />
                            Run AI Financial Analysis
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <ResultsDisplay results={financialAIResults} processingTime={financialAIProcessingTime} />
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-between items-center">
              <button
                onClick={() => { setSelectedPatient(null); navigate('/claims'); }}
                className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-800 font-medium"
              >
                <FileText className="w-4 h-4" /> View in Claims Pipeline
              </button>
              <button onClick={() => setSelectedPatient(null)} className="px-6 py-2 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                Close
              </button>
            </div>
          </div></div>
        </div>
      )}

      {/* Add Patient Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Plus className="w-4 h-4 text-teal-700" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">Add New Patient</h2>
              </div>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1"><div className="p-6 space-y-4">
              {addError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />{addError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">First Name *</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={addForm.first_name} onChange={e => setAddForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="First name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Last Name *</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={addForm.last_name} onChange={e => setAddForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Last name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Date of Birth</label>
                  <input type="date" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={addForm.date_of_birth} onChange={e => setAddForm(f => ({ ...f, date_of_birth: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={addForm.phone} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="(555) 555-5555" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="patient@email.com" />
                </div>
              </div>

              {/* Insurance section */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-teal-600" />
                  <p className="text-sm font-semibold text-slate-700">Insurance</p>
                  <button
                    onClick={() => setAutoCheckEligibility(v => !v)}
                    className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 border transition-colors ${autoCheckEligibility ? 'bg-teal-50 text-teal-600 border-teal-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
                  >
                    <Zap className="w-3 h-3" /> {autoCheckEligibility ? 'Eligibility Verification ON' : 'Eligibility Verification OFF'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Insurance Carrier</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={addForm.insurance_primary_carrier} onChange={e => setAddForm(f => ({ ...f, insurance_primary_carrier: e.target.value }))}
                      placeholder="e.g. Delta Dental, MetLife" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Member ID</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={addForm.insurance_member_id} onChange={e => setAddForm(f => ({ ...f, insurance_member_id: e.target.value }))}
                      placeholder="Insurance member ID" />
                  </div>
                </div>
                <p className="text-xs text-slate-400">Toggle Auto-verify ON to ping pVerify when the patient is saved. Requires pVerify credentials to be configured.</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Secondary Insurance (Optional)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Secondary Carrier</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={addForm.secondary_insurance_carrier} onChange={e => setAddForm(f => ({ ...f, secondary_insurance_carrier: e.target.value }))}
                      placeholder="e.g. Aetna, Cigna" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Member ID</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={addForm.secondary_insurance_id} onChange={e => setAddForm(f => ({ ...f, secondary_insurance_id: e.target.value }))}
                      placeholder="Secondary member ID" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Group Number</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={addForm.secondary_group_number} onChange={e => setAddForm(f => ({ ...f, secondary_group_number: e.target.value }))}
                      placeholder="Group #" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                <textarea rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  value={addForm.notes} onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes" />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={handleAddPatient} disabled={addSaving}
                className="flex items-center gap-2 px-5 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium hover:bg-teal-800 disabled:opacity-50">
                {addSaving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4" /> Add Patient</>}
              </button>
            </div>
          </div></div>
        </div>
      )}

      {/* Edit Patient Modal */}
      {showEditModal && editPatient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Pencil className="w-4 h-4 text-slate-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">Edit Patient</h2>
              </div>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1"><div className="p-6 space-y-4">
              {editError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />{editError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">First Name *</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Last Name *</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Date of Birth</label>
                  <input type="date" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={editForm.date_of_birth} onChange={e => setEditForm(f => ({ ...f, date_of_birth: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              </div>

              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-teal-600" />
                  <p className="text-sm font-semibold text-slate-700">Primary Insurance</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Insurance Carrier</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={editForm.insurance_primary_carrier} onChange={e => setEditForm(f => ({ ...f, insurance_primary_carrier: e.target.value }))} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Member ID</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={editForm.insurance_member_id} onChange={e => setEditForm(f => ({ ...f, insurance_member_id: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                <textarea rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={handleEditPatient} disabled={editSaving}
                className="flex items-center gap-2 px-5 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium hover:bg-teal-800 disabled:opacity-50">
                {editSaving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</> : <><CheckCircle className="w-4 h-4" /> Save Changes</>}
              </button>
            </div>
          </div></div>
        </div>
      )}

      {/* Eligibility Result Toast */}
      {eligibilityResult && (
        <div className="fixed bottom-6 right-6 z-50 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 w-80">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-teal-600" />
              <p className="font-semibold text-slate-800 text-sm">Eligibility Check</p>
            </div>
            <button onClick={() => setEligibilityResult(null)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          {eligibilityChecking ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <RefreshCw className="w-4 h-4 animate-spin" /> Checking eligibility...
            </div>
          ) : (
            <div className="text-sm text-slate-600 space-y-1">
              {eligibilityResult?.InsuranceStatus && (
                <div className="flex items-center gap-2">
                  {eligibilityResult.InsuranceStatus === 'Active Coverage'
                    ? <CheckCircle className="w-4 h-4 text-green-500" />
                    : <XCircle className="w-4 h-4 text-red-500" />}
                  <span className="font-medium">{eligibilityResult.InsuranceStatus}</span>
                </div>
              )}
              {eligibilityResult?.PlanName && <p>Plan: {eligibilityResult.PlanName}</p>}
              {eligibilityResult?.GroupNumber && <p>Group: {eligibilityResult.GroupNumber}</p>}
              {eligibilityResult?.Deductible && <p>Deductible: {eligibilityResult.Deductible}</p>}
            </div>
          )}
        </div>
      )}
      <style>{`
        @keyframes demoBadgeIn {
          from { opacity: 0; transform: translateY(6px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes pulseGlowGreen {
          0%   { box-shadow: 0 0 0 0   rgba(16,185,129,0.45); }
          70%  { box-shadow: 0 0 0 8px rgba(16,185,129,0); }
          100% { box-shadow: 0 0 0 0   rgba(16,185,129,0); }
        }
      `}</style>
    </div>
  );
};

export default Patients;
